import type { Element } from '../engine/element';
import { SPEC_BY_TYPE } from '../engine/catalog';
import { parseValue } from '../engine/format';
import { SubcktInstance } from '../engine/subckt';
import { Microcontroller } from '../engine/mcu';

import { channelKindsFor, KIND_LABEL, type ChannelKind } from './scope';

/** 오른쪽 속성 패널 + 간이 오실로스코프 */
export class Inspector {
  onEdit: () => void = () => {};
  onOpenModule: (el: SubcktInstance) => void = () => {};
  onAddChannel: (el: Element, kind: ChannelKind) => void = () => {};
  private target: Element | null = null;
  private readout: HTMLElement | null = null;
  private logBox: HTMLElement | null = null;

  constructor(private root: HTMLElement) {
    this.build();
  }

  setTarget(el: Element | null): void {
    if (this.target === el && this.root.childElementCount > 0) return;
    this.target = el;
    this.build();
  }

  get current(): Element | null {
    return this.target;
  }

  /* ───────── 속성 폼 ───────── */

  private build(): void {
    this.root.innerHTML = '';
    this.readout = null;
    const el = this.target;
    if (!el) {
      this.root.innerHTML =
        '<div class="empty">왼쪽에서 부품을 골라 캔버스에 끌어다 놓으세요.<br />소자를 클릭하면 여기서 값을 바꿀 수 있어요.</div>';
      return;
    }

    const spec = SPEC_BY_TYPE[el.type];
    const title = document.createElement('div');
    title.className = 'readout head';
    title.textContent = spec?.label ?? el.type;
    this.root.appendChild(title);

    for (const f of spec?.fields ?? []) {
      const wrap = document.createElement('div');
      wrap.className = f.kind === 'bool' ? 'field inline' : 'field';

      if (f.kind === 'bool') {
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.checked = el.bool(f.key, false);
        input.onchange = () => {
          el.props[f.key] = input.checked;
          this.onEdit();
        };
        const label = document.createElement('label');
        label.textContent = f.label;
        wrap.append(input, label);
      } else if (f.kind === 'select') {
        const label = document.createElement('label');
        label.textContent = f.label;
        const sel = document.createElement('select');
        for (const o of f.options ?? []) {
          const opt = document.createElement('option');
          opt.value = o.value;
          opt.textContent = o.label;
          sel.appendChild(opt);
        }
        sel.value = el.str(f.key, (f.options?.[0]?.value ?? ''));
        sel.onchange = () => {
          el.props[f.key] = sel.value;
          this.onEdit();
        };
        wrap.append(label, sel);
      } else if (f.kind === 'code') {
        const label = document.createElement('label');
        label.textContent = f.label;
        const ta = document.createElement('textarea');
        ta.className = 'code';
        ta.spellcheck = false;
        ta.value = el.str(f.key, '');
        ta.rows = 16;
        ta.wrap = 'off';
        // 탭 키로 들여쓰기
        ta.onkeydown = (e) => {
          if (e.key === 'Tab') {
            e.preventDefault();
            const st = ta.selectionStart;
            ta.setRangeText('  ', st, ta.selectionEnd, 'end');
          }
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) apply();
        };
        const btn = document.createElement('button');
        btn.className = 'btn primary wide';
        btn.textContent = '▶ 코드 적용 (Ctrl+Enter)';
        const apply = () => {
          el.props[f.key] = ta.value;
          this.onEdit();
          btn.textContent = '✓ 적용됨 — 처음부터 다시 실행';
          setTimeout(() => (btn.textContent = '▶ 코드 적용 (Ctrl+Enter)'), 1500);
        };
        btn.onclick = apply;
        wrap.append(label, ta, btn);
      } else if (f.kind === 'text') {
        const label = document.createElement('label');
        label.textContent = f.label;
        const input = document.createElement('input');
        input.type = 'text';
        input.value = el.str(f.key, '');
        input.onchange = () => {
          el.props[f.key] = input.value.trim();
          this.onEdit();
        };
        wrap.append(label, input);
      } else if (f.kind === 'color') {
        const label = document.createElement('label');
        label.textContent = f.label;
        const input = document.createElement('input');
        input.type = 'color';
        input.value = el.str(f.key, '#ff4d4d');
        input.oninput = () => {
          el.props[f.key] = input.value;
          this.onEdit();
        };
        wrap.append(label, input);
      } else {
        const label = document.createElement('label');
        label.textContent = f.unit ? `${f.label} (${f.unit})` : f.label;
        const input = document.createElement('input');
        input.type = 'text';
        input.value = formatField(el.num(f.key, 0));
        const commit = () => {
          const parsed = parseValue(input.value);
          if (parsed === null) {
            input.value = formatField(el.num(f.key, 0));
            return;
          }
          let v = parsed;
          if (f.min !== undefined) v = Math.max(f.min, v);
          if (f.max !== undefined) v = Math.min(f.max, v);
          el.props[f.key] = v;
          input.value = formatField(v);
          this.onEdit();
        };
        input.onchange = commit;
        input.onblur = commit;
        wrap.append(label, input);
      }
      this.root.appendChild(wrap);
    }

    // 파형에 채널 추가
    const kinds = channelKindsFor(el);
    if (kinds.length) {
      const row = document.createElement('div');
      row.className = 'chips';
      const lbl = document.createElement('span');
      lbl.textContent = '📈 파형에 추가';
      row.appendChild(lbl);
      for (const k of kinds) {
        const b = document.createElement('button');
        b.className = 'chip';
        b.textContent = KIND_LABEL[k];
        b.onclick = () => this.onAddChannel(el, k);
        row.appendChild(b);
      }
      this.root.appendChild(row);
    }

    if (el instanceof SubcktInstance) {
      const btn = document.createElement('button');
      btn.className = 'btn wide';
      btn.textContent = '모듈 안 열어보기 · 편집';
      btn.title = '캔버스에서 더블클릭해도 돼요';
      btn.onclick = () => this.onOpenModule(el);
      this.root.appendChild(btn);
    }

    const readout = document.createElement('div');
    readout.className = 'readout';
    this.root.appendChild(readout);
    this.readout = readout;
    this.logBox = null;
    if (el instanceof Microcontroller) {
      const lbl = document.createElement('div');
      lbl.className = 'panel-title';
      lbl.textContent = '로그 (print)';
      const pre = document.createElement('pre');
      pre.className = 'logbox';
      this.root.append(lbl, pre);
      this.logBox = pre;
    }
    this.refresh();
  }

  /** 매 프레임 측정값 갱신 */
  refresh(): void {
    const el = this.target;
    if (!el || !this.readout) return;
    const lines = el.info();
    if (el.thermalSpec()) {
      const t = el.thermalSpec();
      lines.push(`온도 ${el.temp.toFixed(1)}℃${t?.tmax ? ` (한계 ${t.tmax}℃)` : ''}`);
    }
    if (el.burnt) lines.push(el.type === 'liion' ? '🔥 열폭주 — 초기화하면 복구' : '🔥 타버림 — 초기화하면 복구');
    if (this.logBox && el instanceof Microcontroller) {
      const text = el.logs.slice(-14).join('\n') || '(아직 없음)';
      if (this.logBox.textContent !== text) {
        this.logBox.textContent = text;
        this.logBox.scrollTop = this.logBox.scrollHeight;
      }
    }
    this.readout.innerHTML = lines
      .map((line, i) => (i === 0 ? `<div class="head">${esc(line)}</div>` : `<div>${esc(line)}</div>`))
      .join('');
  }

}

function formatField(v: number): string {
  if (v === 0) return '0';
  const abs = Math.abs(v);
  if (abs >= 1e-4 && abs < 1e6) return String(parseFloat(v.toPrecision(6)));
  return v.toExponential(3);
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] ?? c);
}
